import { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import { Edit, Shield, Users, Plus, Trash2, Loader2, Crown, Settings, KeyRound, ArrowRightLeft } from 'lucide-react';
import { toast } from 'sonner';
import { usePermissions } from '../context/PermissionsContext';
import AccessDenied from '../components/AccessDenied';
import RoleSettingsPanel from './RoleSettingsPanel';
import UserPermissionsModal from './UserPermissionsModal';
import TransferLeadsModal from './TransferLeadsModal';
import { logger } from '../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROLES = [
  { value: 'admin', label: 'Admin', description: 'Full system access' },
  { value: 'sales_manager', label: 'Sales Manager', description: 'Manage team and all sales data' },
  { value: 'account_executive', label: 'Account Executive', description: 'Manage own leads and deals' },
  { value: 'finance', label: 'Finance', description: 'Manage installments and payments' },
  { value: 'top_management', label: 'Top Management', description: 'View all data (read-only)' }
];

const roleColors = {
  owner: 'bg-gold/20 text-gold border border-gold',
  admin: 'bg-red-100 text-red-800',
  sales_manager: 'bg-purple-100 text-purple-800',
  account_executive: 'bg-blue-100 text-blue-800',
  finance: 'bg-green-100 text-green-800',
  top_management: 'bg-amber-100 text-amber-800',
  pending: 'bg-slate-100 text-slate-600'
};

const statusColors = {
  active: 'bg-green-100 text-green-800',
  pending: 'bg-amber-100 text-amber-800',
  disabled: 'bg-red-100 text-red-800'
};

export default function UsersPage() {
  const { hasPermission, role, loading: permLoading } = usePermissions();
  const [accessDenied, setAccessDenied] = useState(false);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isTransferOpen, setIsTransferOpen] = useState(false);
  const [transferSourceUser, setTransferSourceUser] = useState(null);
  const [transferForceDeactivate, setTransferForceDeactivate] = useState(false);
  const [deleteBlockedReason, setDeleteBlockedReason] = useState('');
  const [formData, setFormData] = useState({
    role: '',
    is_active: true,
    status: 'active',
    clear_overrides: false,
    name: '',
    email: '',
    phone: '',
    job_title: '',
    bio: '',
  });
  const [createFormData, setCreateFormData] = useState({
    name: '',
    email: '',
    role: 'account_executive',
    send_notification: true
  });

  const canViewUsers = hasPermission('users_view');
  const canUpdateUsers = hasPermission('users_update');
  const isOwner = role === 'owner';
  const isAdmin = role === 'admin';
  const [activeTab, setActiveTab] = useState('users');
  const [permModalOpen, setPermModalOpen] = useState(false);
  const [permTargetUser, setPermTargetUser] = useState(null);
  const [usersWithOverrides, setUsersWithOverrides] = useState([]);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/users`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      } else if (response.status === 403) {
        setAccessDenied(true);
      }
    } catch (error) {
      logger.error('Error fetching users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchOverridesList = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/user-permissions/list/with-overrides`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setUsersWithOverrides(data.user_ids || []);
      }
    } catch (err) {
      logger.error('Failed to fetch overrides list:', err);
    }
  }, []);

  useEffect(() => {
    if (!permLoading) {
      if (!canViewUsers) {
        setAccessDenied(true);
        setLoading(false);
      } else {
        fetchUsers();
        if (isOwner || isAdmin) fetchOverridesList();
      }
    }
  }, [permLoading, canViewUsers, fetchUsers, fetchOverridesList, isOwner, isAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleUpdateUser = async () => {
    setIsProcessing(true);
    try {
      // Only send changed fields (avoid sending email when unchanged so
      // "Only Owner can change email" guard isn't tripped unnecessarily)
      const payload = {
        role: formData.role,
        status: formData.status,
        is_active: formData.status === 'active',
        name: formData.name,
        phone: formData.phone,
        job_title: formData.job_title,
        bio: formData.bio,
      };
      if (selectedUser && formData.email && formData.email.trim().toLowerCase() !== (selectedUser.email || '').toLowerCase()) {
        payload.email = formData.email.trim();
      }

      const response = await fetch(`${API_URL}/api/users/${selectedUser.user_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        credentials: 'include'
      });

      if (response.ok) {
        // Clear overrides if role changed and user opted to clear
        const roleChanged = formData.role !== selectedUser.role;
        if (roleChanged && formData.clear_overrides && usersWithOverrides.includes(selectedUser.user_id)) {
          await fetch(`${API_URL}/api/user-permissions/${selectedUser.user_id}`, {
            method: 'DELETE', credentials: 'include'
          });
        }
        toast.success('User updated successfully');
        setIsEditOpen(false);
        fetchUsers();
        fetchOverridesList();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update user');
      }
    } catch (error) {
      logger.error('Error updating user:', error);
      toast.error('Failed to update user');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCreateUser = async () => {
    if (!createFormData.name || !createFormData.email || !createFormData.role) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsProcessing(true);
    try {
      const response = await fetch(`${API_URL}/api/users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(createFormData),
        credentials: 'include'
      });

      if (response.ok) {
        toast.success(`User ${createFormData.name} created successfully`);
        setIsCreateOpen(false);
        setCreateFormData({ name: '', email: '', role: 'account_executive', send_notification: true });
        fetchUsers();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create user');
      }
    } catch (error) {
      logger.error('Error creating user:', error);
      toast.error('Failed to create user');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;

    setIsProcessing(true);
    try {
      const response = await fetch(`${API_URL}/api/users/${selectedUser.user_id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success(`User ${selectedUser.name} deleted`);
        setIsDeleteOpen(false);
        setSelectedUser(null);
        setDeleteBlockedReason('');
        fetchUsers();
      } else {
        const error = await response.json();
        if (response.status === 409) {
          // Ownership guard triggered — prompt transfer
          setDeleteBlockedReason(error.detail || 'User still owns active data');
          toast.error('Transfer this user\'s leads first');
        } else {
          toast.error(error.detail || 'Failed to delete user');
        }
      }
    } catch (error) {
      logger.error('Error deleting user:', error);
      toast.error('Failed to delete user');
    } finally {
      setIsProcessing(false);
    }
  };

  const openTransferForUser = (user, { forceDeactivate = false } = {}) => {
    setTransferSourceUser(user);
    setTransferForceDeactivate(forceDeactivate);
    setIsTransferOpen(true);
  };

  const handleTransferSuccess = (result) => {
    // Refresh users so the ownership state updates
    fetchUsers();
    // If the source user was auto-deactivated + delete flow was open, we can now delete cleanly
    if (result?.source_deactivated && isDeleteOpen) {
      setDeleteBlockedReason('');
    }
  };

  const openEditDialog = (user) => {
    setSelectedUser(user);
    setFormData({
      role: user.role,
      status: user.status || 'active',
      is_active: user.is_active !== false,
      clear_overrides: true,
      name: user.name || '',
      email: user.email || '',
      phone: user.phone || '',
      job_title: user.job_title || '',
      bio: user.bio || '',
    });
    setIsEditOpen(true);
  };

  const openDeleteDialog = (user) => {
    setSelectedUser(user);
    setDeleteBlockedReason('');
    setIsDeleteOpen(true);
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getRoleLabel = (roleValue) => {
    if (roleValue === 'owner') return 'Owner';
    if (roleValue === 'pending') return 'Pending';
    const r = ROLES.find(r => r.value === roleValue);
    return r?.label || roleValue;
  };

  // Count users by role (including owner and pending)
  const allRoles = [
    { value: 'owner', label: 'Owner' },
    ...ROLES,
    { value: 'pending', label: 'Pending' }
  ];
  const roleCounts = allRoles.map(r => ({
    ...r,
    count: users.filter(u => u.role === r.value).length
  })).filter(r => r.count > 0);

  if (loading || permLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gold"></div>
      </div>
    );
  }

  // Show Access Denied for Account Executives
  if (accessDenied) {
    return <AccessDenied message="Account Executives do not have access to user management. Please contact your Admin." />;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="users-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-semibold text-slate-900">User Management</h1>
          <p className="text-slate-500 font-body mt-1">Manage user roles and access permissions</p>
        </div>
        {isOwner && activeTab === 'users' && (
          <Button onClick={() => setIsCreateOpen(true)} data-testid="add-user-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add User
          </Button>
        )}
      </div>

      {/* Tabs — only show if Owner or Admin */}
      {(isOwner || isAdmin) && (
        <div className="flex gap-1 border-b border-slate-200" data-testid="users-tabs">
          <button
            onClick={() => setActiveTab('users')}
            className={`px-4 py-2.5 text-sm font-medium transition-all border-b-2 -mb-px ${
              activeTab === 'users'
                ? 'border-slate-900 text-slate-900'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
            data-testid="tab-users"
          >
            <Users className="w-4 h-4 inline-block mr-1.5 -mt-0.5" />
            Users
          </button>
          <button
            onClick={() => setActiveTab('role_settings')}
            className={`px-4 py-2.5 text-sm font-medium transition-all border-b-2 -mb-px ${
              activeTab === 'role_settings'
                ? 'border-slate-900 text-slate-900'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
            data-testid="tab-role-settings"
          >
            <Settings className="w-4 h-4 inline-block mr-1.5 -mt-0.5" />
            Role Settings
          </button>
        </div>
      )}

      {/* Tab Content */}
      {activeTab === 'role_settings' && (isOwner || isAdmin) ? (
        <RoleSettingsPanel currentUserRole={role} />
      ) : (
      <>
      {/* Role Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {roleCounts.map(role => (
          <Card key={role.value} className="card-goldsands">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">{role.label}</p>
                  <p className="text-2xl font-semibold text-slate-900">{role.count}</p>
                </div>
                <div className={`p-2 rounded-lg ${roleColors[role.value]}`}>
                  <Shield size={16} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Users Table */}
      <Card className="card-goldsands">
        <CardHeader className="border-b border-slate-50 bg-slate-50/50">
          <div className="flex items-center gap-2">
            <Users className="text-gold" size={20} />
            <CardTitle className="font-heading text-lg">All Users ({users.length})</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>User</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Joined</TableHead>
                <TableHead className="w-24">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                    No users found
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => (
                  <TableRow key={user.user_id} className="table-row-hover">
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar className="h-10 w-10 border border-slate-200">
                          <AvatarImage src={user.picture} alt={user.name} />
                          <AvatarFallback className="bg-gold/10 text-gold">
                            {getInitials(user.name)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{user.name}</span>
                          {user.role === 'owner' && <Crown className="w-4 h-4 text-gold" />}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-500">{user.email}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        <Badge className={`${roleColors[user.role] || 'bg-slate-100 text-slate-600'} capitalize`}>
                          {getRoleLabel(user.role)}
                        </Badge>
                        {usersWithOverrides.includes(user.user_id) && (
                          <Badge className="bg-amber-100 text-amber-700 text-[10px] px-1.5 py-0" data-testid={`custom-badge-${user.user_id}`}>Custom</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={statusColors[user.status] || statusColors.active}>
                        {(user.status || 'active').charAt(0).toUpperCase() + (user.status || 'active').slice(1)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-slate-500">
                      {new Date(user.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        {(isOwner || isAdmin) && user.role !== 'owner' && (
                          <Button
                            variant="ghost"
                            size="icon"
                            title="Customize Permissions"
                            onClick={() => { setPermTargetUser(user); setPermModalOpen(true); }}
                            data-testid={`perm-user-${user.user_id}`}
                          >
                            <KeyRound size={16} className={usersWithOverrides.includes(user.user_id) ? 'text-amber-600' : ''} />
                          </Button>
                        )}
                        {canUpdateUsers && user.role !== 'owner' && (
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => openEditDialog(user)}
                            data-testid={`edit-user-${user.user_id}`}
                          >
                            <Edit size={16} />
                          </Button>
                        )}
                        {isOwner && user.role !== 'owner' && user.role !== 'pending' && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-amber-600 hover:text-amber-800 hover:bg-amber-50"
                            title="Transfer leads to another user"
                            onClick={() => openTransferForUser(user)}
                            data-testid={`transfer-user-${user.user_id}`}
                          >
                            <ArrowRightLeft size={16} />
                          </Button>
                        )}
                        {isOwner && user.role !== 'owner' && (
                          <Button 
                            variant="ghost" 
                            size="icon"
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={() => openDeleteDialog(user)}
                            data-testid={`delete-user-${user.user_id}`}
                          >
                            <Trash2 size={16} />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Edit User</DialogTitle>
            <DialogDescription>
              Owners and Admins can edit any user&apos;s full profile including role and email.
            </DialogDescription>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-5">
              {/* User Info */}
              <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg">
                <Avatar className="h-12 w-12 border border-slate-200">
                  <AvatarImage src={selectedUser.picture} alt={selectedUser.name} />
                  <AvatarFallback className="bg-gold/10 text-gold">
                    {getInitials(formData.name || selectedUser.name)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <p className="font-medium">{selectedUser.name}</p>
                  <p className="text-sm text-slate-500">{selectedUser.email}</p>
                </div>
              </div>

              {/* Profile Fields */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="edit-name" className="text-sm">Full name</Label>
                  <Input
                    id="edit-name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Full name"
                    maxLength={120}
                    data-testid="edit-user-name"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="edit-email" className="text-sm">
                    Email <span className="text-amber-600 text-xs">(⚠ affects Google Sign-In)</span>
                  </Label>
                  <Input
                    id="edit-email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="user@example.com"
                    data-testid="edit-user-email"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="edit-phone" className="text-sm">Phone</Label>
                  <Input
                    id="edit-phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+1 555 555 5555"
                    maxLength={40}
                    data-testid="edit-user-phone"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="edit-job-title" className="text-sm">Job title</Label>
                  <Input
                    id="edit-job-title"
                    value={formData.job_title}
                    onChange={(e) => setFormData({ ...formData, job_title: e.target.value })}
                    placeholder="e.g. Senior AE"
                    maxLength={120}
                    data-testid="edit-user-job-title"
                  />
                </div>
              </div>

              <div className="space-y-1">
                <Label htmlFor="edit-bio" className="text-sm">
                  Bio <span className="text-slate-400 text-xs">({(formData.bio || '').length}/2000)</span>
                </Label>
                <Textarea
                  id="edit-bio"
                  value={formData.bio}
                  onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                  placeholder="Short bio..."
                  rows={2}
                  maxLength={2000}
                  data-testid="edit-user-bio"
                />
              </div>

              {/* Role Selection */}
              <div className="space-y-2">
                <Label>Role</Label>
                <Select value={formData.role} onValueChange={(v) => setFormData({ ...formData, role: v })}>
                  <SelectTrigger data-testid="user-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLES.map(r => (
                      <SelectItem key={r.value} value={r.value}>
                        <div>
                          <p className="font-medium">{r.label}</p>
                          <p className="text-xs text-slate-500">{r.description}</p>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Status Selection */}
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                  <SelectTrigger data-testid="user-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="disabled">Disabled</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">
                  {formData.status === 'active' ? 'User can access the system' : 'User is blocked from accessing'}
                </p>
              </div>

              {/* Override warning on role change */}
              {selectedUser && formData.role !== selectedUser.role && usersWithOverrides.includes(selectedUser.user_id) && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg space-y-2" data-testid="role-change-override-warning">
                  <p className="text-sm text-amber-800 font-medium">This user has custom permission overrides</p>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      checked={formData.clear_overrides}
                      onCheckedChange={(v) => setFormData({ ...formData, clear_overrides: !!v })}
                      data-testid="clear-overrides-checkbox"
                    />
                    <span className="text-sm text-amber-700">Clear custom overrides (start fresh with new role defaults)</span>
                  </label>
                  {!formData.clear_overrides && (
                    <p className="text-xs text-amber-600">Existing overrides will be kept. Review them after the role change.</p>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={() => setIsEditOpen(false)}>
                  Cancel
                </Button>
                <Button className="btn-accent" onClick={handleUpdateUser} disabled={isProcessing} data-testid="save-user-btn">
                  {isProcessing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Save Changes
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Add New User</DialogTitle>
            <DialogDescription>
              Create a new user account. They will receive an email notification with their access details.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Name */}
            <div className="space-y-2">
              <Label htmlFor="create-name">Full Name *</Label>
              <Input
                id="create-name"
                placeholder="John Doe"
                value={createFormData.name}
                onChange={(e) => setCreateFormData({ ...createFormData, name: e.target.value })}
                data-testid="create-user-name"
              />
            </div>

            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="create-email">Email Address *</Label>
              <Input
                id="create-email"
                type="email"
                placeholder="john@example.com"
                value={createFormData.email}
                onChange={(e) => setCreateFormData({ ...createFormData, email: e.target.value })}
                data-testid="create-user-email"
              />
            </div>

            {/* Role */}
            <div className="space-y-2">
              <Label>Role *</Label>
              <Select 
                value={createFormData.role} 
                onValueChange={(v) => setCreateFormData({ ...createFormData, role: v })}
              >
                <SelectTrigger data-testid="create-user-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map(r => (
                    <SelectItem key={r.value} value={r.value}>
                      <div>
                        <p className="font-medium">{r.label}</p>
                        <p className="text-xs text-slate-500">{r.description}</p>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Send Notification */}
            <div className="flex items-center space-x-2">
              <Checkbox
                id="send-notification"
                checked={createFormData.send_notification}
                onCheckedChange={(checked) => setCreateFormData({ ...createFormData, send_notification: checked })}
              />
              <Label htmlFor="send-notification" className="text-sm font-normal cursor-pointer">
                Send welcome email notification
              </Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateUser} disabled={isProcessing} data-testid="create-user-submit">
              {isProcessing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Create User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-red-600">Delete User</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this user? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-4">
              <div className="flex items-center gap-4 p-4 bg-red-50 rounded-lg border border-red-200">
                <Avatar className="h-12 w-12 border border-red-200">
                  <AvatarImage src={selectedUser.picture} alt={selectedUser.name} />
                  <AvatarFallback className="bg-red-100 text-red-600">
                    {getInitials(selectedUser.name)}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-medium text-red-900">{selectedUser.name}</p>
                  <p className="text-sm text-red-600">{selectedUser.email}</p>
                </div>
              </div>
              <p className="text-sm text-slate-600">
                This will permanently remove the user and all their session data. Any leads, deals, or tasks assigned to them will remain but may need reassignment.
              </p>
              {deleteBlockedReason && (
                <div
                  className="p-3 border border-red-300 bg-red-50 rounded text-sm text-red-800 space-y-2"
                  data-testid="delete-blocked-reason"
                >
                  <div className="font-semibold">Cannot delete yet</div>
                  <div>{deleteBlockedReason}</div>
                  <Button
                    size="sm"
                    className="bg-amber-600 hover:bg-amber-700 text-white"
                    onClick={() => {
                      setIsDeleteOpen(false);
                      openTransferForUser(selectedUser, { forceDeactivate: true });
                    }}
                    data-testid="open-transfer-from-delete"
                  >
                    <ArrowRightLeft className="w-4 h-4 mr-2" /> Transfer Leads Now
                  </Button>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteUser} disabled={isProcessing} data-testid="confirm-delete-user">
              {isProcessing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Delete User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transfer Leads Modal */}
      <TransferLeadsModal
        open={isTransferOpen}
        onClose={() => setIsTransferOpen(false)}
        sourceUser={transferSourceUser}
        allUsers={users}
        onSuccess={handleTransferSuccess}
        forceDeactivate={transferForceDeactivate}
      />

      {/* User Permissions Modal */}
      <UserPermissionsModal
        open={permModalOpen}
        onOpenChange={setPermModalOpen}
        targetUser={permTargetUser}
        onSaved={fetchOverridesList}
      />
      </>
      )}
    </div>
  );
}
